import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PageHeader from '../../components/PageHeader';
import { useTranslation } from 'react-i18next';
import { ExpensesTable } from '../../pages/Expenses/ExpensesTable';
import { ExpenseSidebar } from '../../pages/Expenses/ExpenseSidebar/ExpenseSidebar';
import {
  ExpenseStatus,
  useManagerExpensesQuery,
  useUpdateExpenseStatusMutation,
  useUpdateExpenseGroupStatusMutation,
  UpdateExpenseGroupStatusInput,
  ExpenseSortFieldEnum,
  AscDescEnum,
  ExpenseTypeFilter,
} from '../../__generated__/graphql';
import {
  Expense,
  ExpenseStatusInfo,
  ExpenseGroupStatusInfo,
  ExpenseFilter,
  emptyExpenseFilter,
} from '../../pages/Expenses/types';
import { mapExpense } from '../../pages/Expenses/mappings';
import ServerError from '../../../../components/ServerError';
import { useToasts } from '../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import { SkeletonTable } from '../../components/Skeleton/Table';
import { routes as mainRoutes } from '../../routes';
import {
  mapManagerReportsType,
  useManagerReportsMode,
} from '../../hooks/useManagerReportsMode';
import { ManagerReportsModeSwitch } from '../components/ManagerReportsModeSwitch';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useHistory, useParams } from 'react-router-dom';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import { useSelectedItemWithComments } from '../../components/Comment';
import { Item } from '../../pages/Expenses/useExpenseTableRenderSettings';
import { useInifinityScroll } from '../../../../components/loadingMoreItemsSpinner/useInfinityScroll';
import { buildExpenseQueryParams } from '../../pages/Expenses/queryParamsHelpers';
import { useFlairPersistentFilter } from '../../../../hooks/useFlairPersistentFilter';
import { FlairLocalStorageKey } from '../../hooks/useFlairLocalStorage';

const Expenses: React.FC = () => {
  const history = useHistory();
  const { id: routeExpenseId } = useParams<{ id: string }>();
  const { defaultSelectedItem: selectedItemWithComments } =
    useSelectedItemWithComments();
  const tNavigation = useNamespacedTranslation('navigation.menuItems.expenses');
  useFlairBreadcrumbHook([{ label: tNavigation('title') }]);

  const [reportsMode, setReportsMode] = useManagerReportsMode('expenses');
  const [filter, setFilter] = useFlairPersistentFilter<ExpenseFilter>({
    defaultFilter: emptyExpenseFilter,
    storageKey: 'manager_expense_filter' as FlairLocalStorageKey,
    createRouteUrl: (queryParams) =>
      mainRoutes.expenses.route.withQueryParams(queryParams).create({}),
  });

  const PAGE_SIZE = 10;
  const previousFiltersRef = useRef<string>('');

  const mapFilterType = (type: string | null): ExpenseTypeFilter | null => {
    if (type === 'individual') return ExpenseTypeFilter.Individual;
    if (type === 'groups') return ExpenseTypeFilter.Groups;
    if (type === 'trips') return ExpenseTypeFilter.Trips;
    return null;
  };

  const mapFilterStatus = (status: string | null): ExpenseStatus | null => {
    if (!status) return null;
    if (Object.values(ExpenseStatus).includes(status as ExpenseStatus)) {
      return status as ExpenseStatus;
    }
    return null;
  };

  const { data, loading, error, fetchMore, refetch } = useManagerExpensesQuery({
    variables: {
      expenseFilter: {
        managerReportsType: mapManagerReportsType(reportsMode),
        category: filter.category || null,
        status: mapFilterStatus(filter.status),
        year: filter.year || null,
        type: mapFilterType(filter.type),
      },
      expenseSort: [
        { field: ExpenseSortFieldEnum.ExpenseDate, order: AscDescEnum.Asc },
      ],
      expensePagination: { offset: 0, limit: PAGE_SIZE },
      groupFilter: {
        managerReportsType: mapManagerReportsType(reportsMode),
        expenseGroupIds: [],
      },
    },
  });

  useEffect(() => {
    if (!loading && data?.manager.expenses.items) {
      const neededGroupIds = new Set<string>();
      data.manager.expenses.items.forEach((expense) => {
        if (expense.flair__Expense_Group__c) {
          neededGroupIds.add(expense.flair__Expense_Group__c);
        }
      });

      const loadedGroupIds = new Set(
        data.manager.expenseGroups?.map((g) => g.Id) || [],
      );

      const missingGroupIds = Array.from(neededGroupIds).filter(
        (id) => !loadedGroupIds.has(id),
      );

      if (missingGroupIds.length > 0) {
        fetchMore({
          variables: {
            groupFilter: {
              managerReportsType: mapManagerReportsType(reportsMode),
              expenseGroupIds: missingGroupIds,
            },
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            const existingGroupIds = new Set(
              prev.manager.expenseGroups?.map((g) => g.Id) || [],
            );
            const newGroups =
              fetchMoreResult.manager.expenseGroups?.filter(
                (group) => !existingGroupIds.has(group.Id),
              ) || [];

            return {
              ...prev,
              manager: {
                ...prev.manager,
                expenseGroups: [
                  ...(prev.manager.expenseGroups || []),
                  ...newGroups,
                ],
              },
            };
          },
        });
      }
    }
  }, [loading, data, fetchMore, reportsMode]);

  const [updateExpenseStatus] = useUpdateExpenseStatusMutation();
  const [updateExpenseGroupStatus] = useUpdateExpenseGroupStatusMutation();
  const [updatingItems, setUpdatingItems] = useState<ExpenseStatusInfo[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const expandedParam = urlParams.get('expanded');
    return expandedParam
      ? new Set(expandedParam.split(',').filter(Boolean))
      : new Set();
  });

  const handleExpandedGroupsChange = useCallback(
    (groups: Set<string>) => {
      setExpandedGroups(groups);

      const currentQueryParams = Object.fromEntries(
        new URLSearchParams(window.location.search),
      );

      const allQueryParams = buildExpenseQueryParams({
        item: null,
        expandedGroups: groups,
        currentQueryParams,
      });

      history.replace(
        mainRoutes.expenses.route.withQueryParams(allQueryParams).create({}),
      );
    },
    [history],
  );
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();

  const { t } = useTranslation();

  const expenseStatusChange = useCallback(
    (info: ExpenseStatusInfo) => {
      setUpdatingItems((prev) => [...prev, info]);
      return updateExpenseStatus({
        variables: {
          input: {
            id: info.expenseId,
            status: info.status as ExpenseStatus,
            declineReason: '',
          },
        },
      })
        .then(() => {
          addSuccess(t('expenses.statusUpdatedToast'));
        })
        .catch(errorHandler)
        .finally(() => {
          setUpdatingItems((prev) =>
            prev.filter((x) => x.expenseId !== info.expenseId),
          );
        });
    },
    [updateExpenseStatus, setUpdatingItems, addSuccess, errorHandler, t],
  );

  const handleStatusChange = useCallback(
    (info: ExpenseStatusInfo) => {
      return expenseStatusChange(info);
    },
    [expenseStatusChange],
  );

  const handleExpenseGroupStatusChange = useCallback(
    async (info: ExpenseGroupStatusInfo) => {
      try {
        const result = await updateExpenseGroupStatus({
          variables: {
            input: {
              id: info.expenseGroupId,
              status: info.status,
              declineReason: info.declineReason,
              reimbursed: info.reimbursed,
            } as UpdateExpenseGroupStatusInput,
          },
        });

        if (result.data?.expenseGroups.updateExpenseGroupStatus.error) {
          throw new Error(
            result.data.expenseGroups.updateExpenseGroupStatus.error,
          );
        }

        addSuccess(t('expenses.statusUpdatedToast'));
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    [updateExpenseGroupStatus, addSuccess, errorHandler, t],
  );

  const expenseGroupMap = useMemo(() => {
    type QueryExpenseGroup = NonNullable<
      typeof data
    >['manager']['expenseGroups'][number];
    const groupMap = new Map<string, QueryExpenseGroup>();
    data?.manager.expenseGroups?.forEach((group) => {
      if (
        group.flair__Status__c !== 'DRAFT' &&
        group.expenses &&
        group.expenses.length > 0
      ) {
        groupMap.set(group.Id, group);
      }
    });
    return groupMap;
  }, [data]);

  const { allExpenses, allExpenseGroups, itemDisplayOrder } = useMemo(() => {
    type QueryExpenseGroup = NonNullable<
      typeof data
    >['manager']['expenseGroups'][number];
    const expenses: Expense[] = [];
    const groups: QueryExpenseGroup[] = [];
    const displayOrder: Array<{ type: 'group' | 'expense'; id: string }> = [];
    const seenGroupIds = new Set<string>();
    const seenExpenseIds = new Set<string>();

    data?.manager.expenses.items.forEach((expenseData) => {
      const expense = mapExpense(expenseData);

      if (seenExpenseIds.has(expense.id)) {
        return;
      }
      seenExpenseIds.add(expense.id);
      expenses.push(expense);

      if (expense.flair__Expense_Group__c) {
        const groupId = expense.flair__Expense_Group__c;
        if (!seenGroupIds.has(groupId) && expenseGroupMap.has(groupId)) {
          seenGroupIds.add(groupId);
          groups.push(expenseGroupMap.get(groupId)!);
          displayOrder.push({ type: 'group', id: groupId });
        }
      } else {
        displayOrder.push({ type: 'expense', id: expense.id });
      }
    });

    return {
      allExpenses: expenses,
      allExpenseGroups: groups,
      itemDisplayOrder: displayOrder,
    };
  }, [data, expenseGroupMap]);

  const expensesPaginationInfo = useMemo(() => {
    return data?.manager.expenses;
  }, [data]);

  const { LoadingMore } = useInifinityScroll({
    objectWithPaginationInfo: expensesPaginationInfo,
    fetchMore: (offset) => {
      const currentGroupIds = new Set<string>();
      data?.manager.expenses.items.forEach((expense) => {
        if (expense.flair__Expense_Group__c) {
          currentGroupIds.add(expense.flair__Expense_Group__c);
        }
      });

      return fetchMore({
        variables: {
          expensePagination: { offset, limit: PAGE_SIZE },
          groupFilter: {
            managerReportsType: mapManagerReportsType(reportsMode),
            expenseGroupIds: Array.from(currentGroupIds),
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const existingExpenseIds = new Set(
            prev.manager.expenses.items.map((e) => e.Id),
          );
          const newExpenses = fetchMoreResult.manager.expenses.items.filter(
            (expense) => !existingExpenseIds.has(expense.Id),
          );
          const mergedExpenses = [
            ...prev.manager.expenses.items,
            ...newExpenses,
          ];

          const newGroupIds = new Set<string>();
          newExpenses.forEach((expense) => {
            if (expense.flair__Expense_Group__c) {
              newGroupIds.add(expense.flair__Expense_Group__c);
            }
          });

          const existingGroupIds = new Set(
            prev.manager.expenseGroups?.map((g) => g.Id) || [],
          );
          const newGroups =
            fetchMoreResult.manager.expenseGroups?.filter(
              (group) =>
                !existingGroupIds.has(group.Id) && newGroupIds.has(group.Id),
            ) || [];

          return {
            ...prev,
            manager: {
              ...prev.manager,
              expenses: {
                ...fetchMoreResult.manager.expenses,
                items: mergedExpenses,
              },
              expenseGroups: [
                ...(prev.manager.expenseGroups || []),
                ...newGroups,
              ],
            },
          };
        },
      }).then();
    },
  });

  useEffect(() => {
    const currentFilters = JSON.stringify({
      category: filter.category,
      status: filter.status,
      year: filter.year,
      type: filter.type,
      reportsMode,
    });

    if (
      currentFilters !== previousFiltersRef.current &&
      previousFiltersRef.current !== ''
    ) {
      previousFiltersRef.current = currentFilters;

      if (expandedGroups.size > 0) {
        setTimeout(() => {
          handleExpandedGroupsChange(new Set());
        }, 0);
      }

      refetch({
        expenseFilter: {
          managerReportsType: mapManagerReportsType(reportsMode),
          category: filter.category || null,
          status: mapFilterStatus(filter.status),
          year: filter.year || null,
          type: mapFilterType(filter.type),
        },
        expenseSort: [
          { field: ExpenseSortFieldEnum.ExpenseDate, order: AscDescEnum.Asc },
        ],
        expensePagination: { offset: 0, limit: PAGE_SIZE },
        groupFilter: {
          managerReportsType: mapManagerReportsType(reportsMode),
          expenseGroupIds: [],
        },
      });
    } else if (previousFiltersRef.current === '') {
      previousFiltersRef.current = currentFilters;
    }
  }, [
    filter.category,
    filter.status,
    filter.year,
    filter.type,
    refetch,
    reportsMode,
    handleExpandedGroupsChange,
    expandedGroups.size,
  ]);

  useEffect(() => {
    if (!loading && data && window.location.hash) {
      const targetId = window.location.hash.substring(1);

      const scrollToElement = () => {
        const element = document.getElementById(targetId);
        if (element) {
          const elementTop =
            element.getBoundingClientRect().top + window.pageYOffset;
          const offsetTop = elementTop - 4 * 16;

          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
          return true;
        }
        return false;
      };

      const attemptScroll = () => {
        if (!scrollToElement()) {
          setTimeout(attemptScroll, 100);
        }
      };

      setTimeout(attemptScroll, 300);
    }
  }, [loading, data, expenseGroupMap]);

  if (error) {
    return <ServerError />;
  }

  const handleOnItemClick = (item: Maybe<Item>) => {
    const currentQueryParams = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    const allQueryParams = buildExpenseQueryParams({
      item,
      expandedGroups,
      currentQueryParams,
    });

    if (item?.id) {
      history.push(
        mainRoutes.expense.route
          .withQueryParams(allQueryParams)
          .create({ id: item.id }),
      );
    } else {
      history.push(
        mainRoutes.expenses.route.withQueryParams(allQueryParams).create({}),
      );
    }
  };

  const renderContent = () => {
    if (loading || !data) {
      return <Loading />;
    }

    return (
      <>
        <ExpensesTable
          onItemClick={handleOnItemClick}
          expenses={allExpenses}
          expenseGroups={allExpenseGroups}
          itemDisplayOrder={itemDisplayOrder}
          updatingItems={updatingItems}
          onStatusChange={handleStatusChange}
          onExpenseGroupStatusChange={handleExpenseGroupStatusChange}
          filterStorageKey="manager_expense_filter"
          createRouteUrl={(queryParams) =>
            mainRoutes.expenses.route.withQueryParams(queryParams).create({})
          }
          expandedGroups={expandedGroups}
          onExpandedGroupsChange={handleExpandedGroupsChange}
          filter={filter}
          setFilter={setFilter}
        />
        {LoadingMore}
      </>
    );
  };

  const selectedItemId = routeExpenseId ?? selectedItemWithComments?.id;
  const showComments = selectedItemWithComments?.showComments ?? false;
  const urlParams = new URLSearchParams(window.location.search);
  const showItems = urlParams.get('showItems') === 'true';
  let item = allExpenses?.find((x) => x.id === selectedItemId) ?? null;
  return (
    <>
      <PageHeader
        title={t('expenses.title')}
        actions={
          <ManagerReportsModeSwitch
            value={reportsMode}
            onChange={setReportsMode}
          />
        }
      />
      {renderContent()}
      {item && (
        <ExpenseSidebar
          isManager={true}
          expense={item}
          onStatusChange={handleStatusChange}
          onClose={() => handleOnItemClick(null)}
          showComments={showComments}
          showItems={showItems}
        />
      )}
    </>
  );
};

const Loading: React.FC = () => {
  return <SkeletonTable columns={5} rows={5} />;
};

export default Expenses;
